import React, { useEffect, useState } from "react";
import { Check, Copy, KeyRound, Mail, Pencil } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, DetailRow, DetailSection, IconButton, StatusBadge,
} from "../../../ui";
import IdentityCardSection from "../../../IdentityCard/IdentityCardSection";
import { DASH, formatDate, formatDateTimeFriendly, orDash } from "../../../../utils/format";
import RecordCardHead from "../RecordCardHead";
import { ageFrom } from "../studentRecord";
import { experienceLabel, experienceMonths } from "../../profile/studentProfileRules";

const PLACEMENT_WANTED = { Yes: "Wanted", No: "Not wanted" };

const rupees = (n) => (n == null || n === "" ? null : `₹${Number(n).toLocaleString("en-IN")}`);

/** The record id, and a way to copy it into a support ticket. */
const RecordId = ({ id }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopied(true);
    } catch {
      /* clipboard refused (insecure origin, permissions) — the id is still selectable */
    }
  };

  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1">
      <span className="tw-font-mono tw-text-xs tw-text-content-muted">{id}</span>
      <IconButton label={copied ? "Copied" : "Copy record ID"} icon={copied ? Check : Copy} size="xs" onClick={copy} />
    </span>
  );
};

/** A card of label / value rows under one heading. */
const FactsCard = ({ title, description, action, children }) => (
  <AppCard>
    <RecordCardHead title={title} description={description} action={action} />
    <AppCardContent>
      <DetailSection className="tw-mb-0">{children}</DetailSection>
    </AppCardContent>
  </AppCard>
);

/**
 * ProfileTab — the full student record: everything the admission form holds,
 * the login behind it, and the ID card.
 *
 * Props:
 *   student      — the `GET /students/:id` student
 *   accountState — studentRecord.accountState()
 *   activity     — studentRecord.engagement()
 *   onEdit       — omitted when the viewer may not edit
 *   onResetPassword / onResendWelcome — omitted when not allowed or not possible
 */
const ProfileTab = ({ student, accountState, activity, onEdit, onResetPassword, onResendWelcome }) => {
  const age = ageFrom(student.dob);
  const hasGuardian = student.guardianName || student.guardianMobile || student.guardianEmail;
  const hasWork = student.department || student.jobRole || student.employeeId;
  const editAction = onEdit && (
    <AppButton size="sm" variant="outline" onClick={onEdit}><Pencil size={16} /> Edit</AppButton>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-grid tw-items-start tw-gap-4 min-[992px]:tw-grid-cols-2">
        <FactsCard title="Personal details" action={editAction}>
          <DetailRow label="Full name" value={student.name} />
          <DetailRow
            label="Date of birth"
            value={student.dob ? `${formatDate(student.dob)}${age !== null ? ` · ${age} years` : ""}` : DASH}
          />
          <DetailRow label="Gender" value={orDash(student.gender)} />
          <DetailRow label="Mobile" value={orDash(student.mobile)} />
          <DetailRow label="Email" value={orDash(student.email)} />
          <DetailRow label="Address" value={orDash(student.address)} />
        </FactsCard>

        <FactsCard
          title="Guardian"
          description={hasGuardian ? "Absence alerts go to this contact when they are switched on." : "No guardian details on file."}
        >
          <DetailRow label="Name" value={orDash(student.guardianName)} />
          <DetailRow label="Mobile" value={orDash(student.guardianMobile)} />
          <DetailRow label="Email" value={orDash(student.guardianEmail)} />
        </FactsCard>

        <FactsCard title="Education and background">
          <DetailRow label="Degree" value={[student.degree, student.degreeBranch].filter(Boolean).join(" — ") || DASH} />
          <DetailRow label="College / University" value={orDash(student.college)} />
          <DetailRow label="Percentage" value={student.percentage != null && student.percentage !== "" ? `${student.percentage}%` : DASH} />
          <DetailRow label="Passed out" value={orDash(student.passout)} />
          <DetailRow label="Experience" value={student.experience ? `${student.experience} years` : DASH} />
          <DetailRow label="Placement support" value={PLACEMENT_WANTED[student.placement] || DASH} />
          <DetailRow
            label="Referred by"
            value={student.refName || student.refNumber
              ? [student.refName, student.refNumber].filter(Boolean).join(" · ")
              : DASH}
          />
        </FactsCard>

        <FactsCard title="Admission">
          <DetailRow label="Joined" value={formatDate(student.joinedDate || student.createdAt)} />
          <DetailRow label="Home branch" value={orDash(student.branchId?.name || student.batch?.branchId?.name)} />
          <DetailRow label="Record created" value={formatDate(student.createdAt)} />
          <DetailRow label="Record ID" value={<RecordId id={student._id} />} />
        </FactsCard>

        {hasWork && (
          <FactsCard title="Work">
            <DetailRow label="Department" value={orDash(student.department)} />
            <DetailRow label="Job role" value={orDash(student.jobRole)} />
            <DetailRow label="Employee ID" value={orDash(student.employeeId)} />
          </FactsCard>
        )}

        {Array.isArray(student.workHistory) && student.workHistory.length > 0 && (
          <FactsCard
            title="Previous companies"
            description={`${experienceLabel(experienceMonths(student.experience))} in all, as the admission form has it.`}
          >
            {student.workHistory.map((w, i) => (
              <DetailRow
                key={w._id || i}
                label={w.company || `Company ${i + 1}`}
                value={[
                  w.designation,
                  w.durationMonths ? experienceLabel(w.durationMonths) : null,
                  rupees(w.salary) ? `${rupees(w.salary)} / yr` : null,
                  w.lastWorkingDay ? `until ${formatDate(w.lastWorkingDay)}` : null,
                ].filter(Boolean).join(" · ") || DASH}
              />
            ))}
          </FactsCard>
        )}

        <FactsCard
          title="Login and access"
          action={(onResetPassword || onResendWelcome) && (
            onResetPassword ? (
              <AppButton size="sm" variant="outline" onClick={onResetPassword}><KeyRound size={16} /> Reset password</AppButton>
            ) : (
              <AppButton size="sm" variant="outline" onClick={onResendWelcome}><Mail size={16} /> Resend welcome email</AppButton>
            )
          )}
        >
          <DetailRow
            label="Login"
            value={accountState ? <StatusBadge status={accountState} domain="studentAccount" /> : DASH}
          />
          <DetailRow
            label="Last active"
            value={activity?.lastActiveAt ? formatDateTimeFriendly(activity.lastActiveAt) : "No activity recorded"}
          />
          <DetailRow
            label="Active days"
            value={activity ? `${activity.activeDays30} of the last 30` : DASH}
          />
        </FactsCard>
      </div>

      {student.comments && (
        <AppCard>
          <RecordCardHead title="Notes" />
          <AppCardContent>
            <p className="tw-m-0 tw-whitespace-pre-line tw-text-sm tw-leading-relaxed tw-text-content">{student.comments}</p>
          </AppCardContent>
        </AppCard>
      )}

      <IdentityCardSection studentId={student._id} title="Student ID card" />
    </div>
  );
};

export default ProfileTab;
